import { useEffect, useRef } from 'react';
import { effectiveTurnstileSiteKey } from '../../lib/config';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

function ensureScript(): Promise<void> {
  return new Promise((resolve) => {
    if (window.turnstile) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    document.head.appendChild(s);
  });
}

interface Props {
  onToken: (token: string) => void;
  onExpire?: () => void;
}

/**
 * Turnstile "flexible" fills its container but never goes below 300px.
 * On narrow phones the form's own padding leaves less than that, and the
 * widget then sticks out to the right of the card — reported from the
 * embedded page. Below the floor we fall back to "compact" (150x140),
 * which always fits.
 *
 * The size is decided once, on mount, and deliberately not re-evaluated
 * on resize: re-rendering the widget would discard a token the visitor
 * has already solved. Both sizes work in either orientation.
 */
const FLEXIBLE_MIN_WIDTH = 300;

function sizeFor(width: number): 'flexible' | 'compact' {
  // A container of zero width means layout has not settled yet — assume
  // there is room rather than dropping to compact for everyone.
  if (width === 0) return 'flexible';
  return width >= FLEXIBLE_MIN_WIDTH ? 'flexible' : 'compact';
}

export default function TurnstileWidget({ onToken, onExpire }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | null>(null);
  const siteKey = effectiveTurnstileSiteKey();

  useEffect(() => {
    if (!siteKey || !ref.current) return;
    let cancelled = false;
    void ensureScript().then(() => {
      if (cancelled || !window.turnstile || !ref.current) return;
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (t) => onToken(t),
        'expired-callback': () => onExpire?.(),
        theme: 'light',
        size: sizeFor(ref.current.clientWidth),
      });
    });
    return () => {
      cancelled = true;
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* noop */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) {
    return <div className="rf-help">Bot-Schutz ist nicht konfiguriert (VITE_TURNSTILE_SITE_KEY).</div>;
  }
  return <div className="rf-turnstile" ref={ref} />;
}
